/**
 * 模版文件分层识别 — 本地 CDP 服务（image / PDF / PSD / CDR）
 * 输出 JSON 字符串，供 POST /api/art/template/import 的 layerJson 字段
 */

import { CDP_ENDPOINTS, fetchCdpEngineJson } from '@/services/cdpEngineClient';

const DEFAULT_TIMEOUT_MS = 120_000;
const PDF_TIMEOUT_MS = 900_000;

function resolveLayerEndpoint(file) {
  const lower = String(file?.name || '').toLowerCase();
  if (/\.pdf$/i.test(lower) || file?.type === 'application/pdf') {
    return { endpoint: CDP_ENDPOINTS.importPdfLayers, field: 'pdf', timeoutMs: PDF_TIMEOUT_MS };
  }
  if (/\.psd$/i.test(lower)) {
    return { endpoint: CDP_ENDPOINTS.importPsdLayers, field: 'psd' };
  }
  if (/\.cdr$/i.test(lower)) {
    return { endpoint: CDP_ENDPOINTS.importCdrLayers, field: 'cdr' };
  }
  return { endpoint: CDP_ENDPOINTS.importImageLayers, field: 'image' };
}

function normalizeLayerJsonValue(layerJson) {
  if (layerJson == null || layerJson === '') return '';
  return typeof layerJson === 'string' ? layerJson.trim() : JSON.stringify(layerJson);
}

export async function extractTemplateLayersFromFile(file, options = {}) {
  if (!file) throw new Error('缺少模版文件');
  const { endpoint, field, timeoutMs: endpointTimeoutMs } = resolveLayerEndpoint(file);
  const formData = new FormData();
  formData.append(field, file, file.name);
  formData.append('dpi', String(options.dpi || 180));

  const timeoutMs = Number(options.timeoutMs) > 0
    ? Number(options.timeoutMs)
    : (endpointTimeoutMs || DEFAULT_TIMEOUT_MS);
  try {
    const payload = await fetchCdpEngineJson(endpoint, {
      method: 'POST',
      body: formData,
    }, {
      timeoutMs,
      fallbackMessage: '分层识别失败',
      timeoutMessage: '分层识别超时，请检查文件复杂度后重试',
    });
    if (payload?.error) {
      throw new Error(payload.error);
    }
    return JSON.stringify(payload);
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error('未找到可用的 Python CDP 分层服务，请确认 VITE_CDP_ENGINE_URL 已配置且服务可访问');
    }
    throw error;
  }
}

/** 按文件类型调用对应分层服务，返回 import 所需 layerJson */
export async function resolveTemplateImportLayers(file, options = {}) {
  const layerJson = await extractTemplateLayersFromFile(file, options);
  const normalized = normalizeLayerJsonValue(layerJson);
  let pageCount = 1;
  let thumb = null;
  try {
    const parsed = JSON.parse(normalized);
    if (Array.isArray(parsed?.pages) && parsed.pages.length > 0) {
      pageCount = parsed.pages.length;
    }
    if (parsed?.coverThumb || parsed?.thumb) {
      thumb = parsed.coverThumb || parsed.thumb;
    }
  } catch { /* ignore */ }
  return {
    layerJson: normalized,
    thumb,
    originFileUrl: null,
    pageCount,
  };
}
